import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomAlert from '../CustomAlert';
import { t } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = {
  name: '',
  address: '',
  email: '',
  phone: '',
  website: '',
  bank: '',
  bank_number: '',
  primary_person: '',
  secondary_person: '',
  tax_id: '',
  bussiness_id: '',
  description: '',
};

/**
 * Partner form. Renders no page chrome so it can sit either in the Drawer on
 * the list page or standalone on the /partners/add route.
 *
 * onSuccess(created) — drawer closes and the list refreshes
 */
export default function AddPartner({ onSuccess }) {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(EMPTY);

  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPartner((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { name, address, phone, email, bank, bank_number, secondary_person, primary_person, tax_id, bussiness_id } =
      partner;

    if (!name || !address || !phone) {
      setAlert({ show: true, message: t('Пополнете ги сите задолжителни полиња.'), type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/partner-add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          address,
          phone,
          email,
          bank,
          bank_number,
          secondary_person,
          primary_person,
          tax_id,
          bussiness_id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.info({ response });
        throw new Error(errorData.error || t('Партнерот не може да се креира'));
      }

      const data = await response.json();
      setPartner(EMPTY);

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/partners/list');
      }
    } catch (error) {
      console.error('Error:', error.message);
      setAlert({ show: true, message: t('Грешка: {message}', { message: error.message }), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // One shared spec per field instead of repeating the same class string 12
  // times. This is a plain function, NOT a component — declaring a component
  // inside render would remount the input on every keystroke and drop focus.
  const LABEL = 'block text-sm font-medium text-gray-700';
  const INPUT =
    'mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  const renderField = ({ label, name, type = 'text', required = false, span }) => (
    <div key={name} className={span}>
      <label className={LABEL}>
        {label}
        {required && ' *'}
      </label>
      <input
        type={type}
        name={name}
        value={partner[name]}
        onChange={handleChange}
        required={required}
        className={INPUT}
      />
    </div>
  );

  return (
    <>
      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? t('Успешно') : t('Грешка')}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Required fields */}
        <div className="border-l-4 border-green-500 pl-3">
          <h2 className="text-base font-semibold text-gray-800">{t('Основни податоци')}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
          {renderField({ label: t('Назив на партнер'), name: 'name', required: true, span: 'sm:col-span-2 lg:col-span-3' })}
          {renderField({ label: t('Адреса'), name: 'address', required: true, span: 'lg:col-span-2' })}
          {renderField({ label: t('Телефон'), name: 'phone', required: true })}
        </div>

        {/* Optional fields */}
        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold focus:outline-none transition-colors"
          >
            <span className="mr-2">{showMore ? '▼' : '▶'}</span>
            {showMore ? t('Сокриј дополнителни полиња') : t('Прикажи дополнителни полиња')}
          </button>

          {showMore && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 mt-3">
              {renderField({ label: t('Даночен број'), name: 'tax_id' })}
              {renderField({ label: t('Матичен број'), name: 'bussiness_id' })}
              {renderField({ label: t('Е-пошта'), name: 'email', type: 'email' })}
              {renderField({ label: t('Веб-страна'), name: 'website' })}
              {renderField({ label: t('Банка'), name: 'bank' })}
              {renderField({ label: t('Жиро сметка'), name: 'bank_number' })}
              {renderField({ label: t('Прв контакт'), name: 'primary_person' })}
              {renderField({ label: t('Втор контакт'), name: 'secondary_person' })}

              <div className="sm:col-span-2 lg:col-span-3">
                <label className={LABEL}>{t('Опис')}</label>
                <textarea
                  name="description"
                  value={partner.description}
                  onChange={handleChange}
                  rows={2}
                  className={INPUT}
                />
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? t('Се зачувува...') : t('Креирај партнер')}
          </button>
        </div>
      </form>
    </>
  );
}
